import { Component } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { ICellRendererAngularComp } from "ag-grid-angular";
import { ICellRendererParams } from "ag-grid-community";
import { environment } from "src/environments/environment";
import { DocumentViewerModalComponent } from "./document-viewer-modal.component";
import { AlertService } from "src/app/_services/alert.service";

const { API_URLBLAZE } = environment;

const MIME_BY_EXT: Record<string, string> = {
  pdf: 'application/pdf',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
  bmp: 'image/bmp',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
};

@Component({
  selector: 'app-documents-cell-renderer',
  templateUrl: './documents-cell-renderer.component.html',
  styleUrls: ['./documents-cell-renderer.component.scss'],
})
export class DocumentsCellRendererComponent implements ICellRendererAngularComp {
  documents: { type: string; path: string; status?: string }[] = [];
  userType: 'MERCHANT' | 'CUSTOMER' = 'MERCHANT';
  merchantId: string;
  updatingDocType: string | null = null;
  merchantStatus: string;
  enableStatusUpdates = true;

  constructor(
    private http: HttpClient,
    private modalService: NgbModal,
    private alertService: AlertService
  ) {}

  agInit(params: ICellRendererParams): void {
    this.documents = params.data?.documents || [];
    this.userType = (params as any).userType ?? params.data?.userType ?? 'MERCHANT';
    this.merchantId = params.data?.userId;
    this.merchantStatus=params.data?.walletStatus;
    this.enableStatusUpdates = (params as any).enableStatusUpdates !== false;
  }

  refresh(params: ICellRendererParams): boolean {
    this.documents = params.data?.documents || [];
    this.userType = (params as any).userType ?? params.data?.userType ?? 'MERCHANT';
    this.merchantId = params.data?.userId;
    this.merchantStatus=params.data?.walletStatus;
    this.enableStatusUpdates = (params as any).enableStatusUpdates !== false;
    return true;
  }

  viewDoc(doc: { type: string; path: string }): void {
    this.http
      .get(`${API_URLBLAZE}/user/files/download`, {
        params: { path: doc.path },
        responseType: 'blob',
        observe: 'response',
      })
      .subscribe((response) => {
        const serverType = response.headers.get('Content-Type') ?? '';
        const ext = doc.path.split('.').pop()?.toLowerCase() ?? '';
        const contentType =
                      serverType &&
                      !serverType.toLowerCase().startsWith('application/octet-stream')
                        ? serverType
                        : MIME_BY_EXT[ext] ?? 'application/pdf';

        const blob = new Blob([response.body!], { type: contentType });
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onloadend = () => {
          const modalRef = this.modalService.open(DocumentViewerModalComponent, {
            size: 'xl',
            centered: true,
          });
          modalRef.componentInstance.dataUrl = reader.result as string;
          modalRef.componentInstance.title = doc.type;
          modalRef.componentInstance.isImage = contentType.startsWith('image/');
          modalRef.componentInstance.contentType = contentType;
        };
      });
  }

  downloadDoc(doc: { type: string; path: string }): void {
    this.http
      .get(`${API_URLBLAZE}/user/files/download`, {
        params: { path: doc.path },
        responseType: 'blob',
        observe: 'response',
      })
      .subscribe((response) => {
        const contentType = response.headers.get('Content-Type') || 'application/octet-stream';
        const blob = new Blob([response.body!], { type: contentType });
        const contentDisposition = response.headers.get('Content-Disposition');
        const fileNameMatch = contentDisposition?.match(/filename="?([^";\n]+)"?/);
        const originalFileName = doc.path.split('/').pop() || doc.type;
        const fileName = fileNameMatch?.[1] ?? originalFileName;

        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = fileName;
        link.click();
        URL.revokeObjectURL(objectUrl);
      });
  }

  updateDocStatus(doc: { type: string; path: string; status?: string }, status: 'ACCEPTED' | 'REJECTED'): void {
    if (this.updatingDocType) {
      return;
    }
    this.updatingDocType = doc.type;
    const isCustomer = this.userType === 'CUSTOMER';
    const endpoint = isCustomer
      ? '/user/customer/updateCustomerDocumentStatus'
      : '/user/merchant/updateMerchantDocumentStatus';
    const request = {
      ...(isCustomer ? { customerId: this.merchantId } : { merchantId: this.merchantId }),
      documentType: doc.type,
      status,
    };
    this.http
      .post(`${API_URLBLAZE}${endpoint}`, request)
      .subscribe({
        next: () => {
          doc.status = status;
          this.updatingDocType = null;
          this.alertService.successAlertWithAcknowledgment(
            `Document ${status === 'ACCEPTED' ? 'accepted' : 'rejected'} successfully`
          );
        },
        error: () => {
          this.updatingDocType = null;
          this.alertService.errorAlertWithMessage('Unable to update document status');
        },
      });
  }
}
